import { supabase, DEFAULT_ORG_ID, getActiveOrgOverride } from '../supabaseClient'
import { getCurrentStaff } from './auth'
import { normalizarTexto } from '../responsableResolver'

// Solo digitos: "871 534-8580" y "8715348580" son el mismo telefono. Coincide
// con la normalizacion del indice parcial de personas (regexp_replace ... '[^0-9]').
export const soloDigitos = (t) => String(t || '').replace(/[^0-9]/g, '')

const orgActiva = async (orgId) =>
  orgId || getActiveOrgOverride() || (await getCurrentStaff())?.data?.org_id || DEFAULT_ORG_ID

// Buscar a quien ya tiene este telefono.
//
// Es una SENAL para preguntarle a un humano, no una llave: el telefono del abuelo
// puede ser legitimamente el de dos familias. Por eso devuelve candidatos, con sus
// jugadores, para que quien captura decida con el dato enfrente.
//
// El filtro va en la BASE (columna generada personas.telefono_norm, migracion
// 20260826_personas_telefono_norm.sql), no en el cliente. Version anterior traia
// hasta 5 personas activas de la org sin ORDER BY ni predicado de telefono y
// filtraba la coincidencia aqui: en una org con pocas personas "funcionaba" de
// pura suerte, pero RAIDERS en PROD tiene 129 personas -> ~4% de probabilidad de
// que la persona buscada cayera en esas 5. La deduplicacion fallaba justo en la
// org que la necesita. .eq('telefono_norm', tel) es una busqueda exacta por
// indice: no importa cuantas personas tenga la org.
export async function buscarPersonaPorTelefono(telefono, orgId = null) {
  const tel = soloDigitos(telefono)
  // Menos de 10 digitos es un telefono a medio escribir: buscar daria falsos.
  if (tel.length < 10) return { data: [], error: null }
  try {
    const org = await orgActiva(orgId)
    const { data, error } = await supabase
      .from('personas')
      .select(`
        id, nombres, apellidos, nombre_completo, telefono, email,
        familia_personas ( rol, es_principal, familias:familia_id ( id, nombre,
          jugadores ( id, nombre_completo ) ) )
      `)
      .eq('org_id', org)
      .eq('activo', true)
      .eq('telefono_norm', tel)
      // Orden ESTABLE y con un criterio que de verdad discrimina. Sin ORDER BY,
      // Postgres puede devolver el mismo telefono compartido en distinto orden entre
      // llamadas, y la lista que se le pone enfrente a un humano para que elija
      // cambiaria sola.
      //
      // NO se ordena por created_at: el backfill de 20260825c inserto a TODAS las
      // personas en una sola sentencia, asi que en PROD las 136 comparten el mismo
      // created_at y los 6 grupos de telefono compartido quedan empatados -- el
      // desempate real seria el id, que es gen_random_uuid(): orden aleatorio con
      // apariencia de criterio. Por nombre si discrimina, y ademas es el orden en el
      // que un humano espera leer una lista de personas.
      .order('nombre_completo', { ascending: true })
      .order('id', { ascending: true })
      // Defensivo, no filtro: el mismo telefono puede ser legitimamente el de
      // varias familias (SENAL, no llave). Va DESPUES del .eq exacto, solo para
      // no regresar una lista sin tope si un numero esta muy compartido.
      .limit(10)
    if (error) throw error
    return { data: data || [], error: null }
  } catch (error) {
    console.error('[buscarPersonaPorTelefono]', error)
    return { data: [], error }
  }
}

// Buscar a una persona MIENTRAS se escribe, por nombre o por telefono.
//
// buscarPersonaPorTelefono() no sirve para esto y se queda como esta: exige 10 digitos
// y coincidencia exacta porque la usa el GUARDADO, donde una coincidencia parcial
// ligaria a la persona equivocada. Esta es para la UI, que necesita empezar a guiar
// desde el tercer caracter -- cuando el capturista todavia puede corregir el rumbo.
//
// Y GUIAR ES TODO LO QUE HACE. Devuelve candidatos; quien decide si son la misma
// persona es un humano, con la tarjeta que ya existe. Es la regla 5, y es lo que costo
// siete defectos aprender: cada vez que el sistema dedujo por su cuenta que dos
// registros eran el mismo, borro a alguien.
export const MIN_BUSQUEDA = 3

// Un telefono es como un humano lo escribe: digitos, espacios, guiones, parentesis.
// "871 411" es un telefono a medio teclear; "Ana 3" es un nombre.
const pareceTelefono = (t) => /^[0-9][0-9\s()+.-]*$/.test(String(t || '').trim())

export async function buscarPersonas(texto, orgId = null) {
  const crudo = String(texto || '')
  const esTelefono = pareceTelefono(crudo)
  // Se normaliza con la MISMA regla con la que el resolver decide si dos nombres son
  // el mismo (normalizarTexto): sin acentos, en minusculas. En PROD el 46% de las
  // personas estan guardadas en MAYUSCULAS o con acentos -- comparando literal no se
  // encuentra justo a quien se esta intentando no duplicar. La base la vuelve a
  // aplicar, porque no puede confiar en que siempre la llamen desde aqui.
  const termino = esTelefono ? soloDigitos(crudo) : normalizarTexto(crudo)
  if (termino.length < MIN_BUSQUEDA) return { data: [], error: null }
  try {
    const org = await orgActiva(orgId)
    // El filtro por nombre vive en la BASE (buscar_personas, migracion 20260830b):
    // PostgREST no puede pedir `unaccent(lower(nombre_completo))` en un filtro, e
    // `ilike` resuelve las mayusculas pero no los acentos -- buscando "gandara" no
    // encontraria a "Ruiz Gándara". Tampoco sirve una columna generada: nombre_completo
    // ya lo es, y Postgres no deja que una generada dependa de otra.
    const { data, error } = await supabase.rpc('buscar_personas', {
      p_texto: termino,
      p_org_id: org,
      // Tope, no paginacion: esto es una sugerencia al lado de un campo. Una lista de
      // treinta nombres no le sugiere nada a nadie -- se sigue escribiendo y se acorta.
      p_limite: 8,
    })
    if (error) throw error
    // Tal cual vienen: el ORDER BY es de la consulta (nombre_completo, id) y reordenar
    // aqui haria que la lista que un humano esta leyendo cambie sola.
    return { data: data || [], error: null }
  } catch (error) {
    console.error('[buscarPersonas]', error)
    return { data: [], error }
  }
}

export async function crearPersona({ nombres, apellidos, telefono, email, orgId = null }) {
  try {
    const org = await orgActiva(orgId)
    const { data, error } = await supabase
      .from('personas')
      .insert({
        org_id: org,
        nombres: String(nombres || '').trim(),
        apellidos: String(apellidos || '').trim(),
        telefono: String(telefono || '').trim() || null,
        email: String(email || '').trim() || null,
      })
      .select()
      .single()
    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[crearPersona]', error)
    return { data: null, error }
  }
}

// El caso central del diseño es papa y mama en la misma familia: el segundo
// responsable principal tiene que poder reemplazar al primero, no chocar con el.
// "es_principal" esta protegido por un indice unico PARCIAL (familia_id, rol)
// WHERE es_principal, que la PK (familia_id, persona_id, rol) no cubre -- un
// upsert directo con onConflict=PK insertaba una fila nueva sin tocar la vieja y
// tronaba con 23505 crudo. El RPC ligar_persona_a_familia hace, en una sola
// sentencia, "quitale principal al que lo tenia, y liga/actualiza a este".
export async function ligarPersonaAFamilia({ familiaId, personaId, rol = 'responsable', esPrincipal = true }) {
  try {
    const { data, error } = await supabase.rpc('ligar_persona_a_familia', {
      p_familia_id: familiaId,
      p_persona_id: personaId,
      p_rol: rol,
      p_es_principal: esPrincipal,
    })
    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[ligarPersonaAFamilia]', error)
    return { data: null, error }
  }
}

// Ligar a una persona como TUTORA LEGAL de un jugador. Es el otro eje: familia_personas
// es de pagos, jugador_personas es de representacion legal, y conviven.
//
// Existe para la salida honesta de "la firma dice otra cosa": cuando quien firmo NO es
// la persona que consta en la ficha, la respuesta correcta no es reescribir a esa
// persona -- es crear a quien firmo y ligarla al menor, sin tocar a nadie mas.
//
// El org_id no se manda: lo pone el trigger desde el jugador, que es tambien quien
// impide ligar a una persona de otra org.
export async function ligarPersonaAJugador({ jugadorId, personaId, parentesco = null }) {
  try {
    const { data, error } = await supabase.rpc('ligar_persona_a_jugador', {
      p_jugador_id: jugadorId,
      p_persona_id: personaId,
      p_parentesco: parentesco,
    })
    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[ligarPersonaAJugador]', error)
    return { data: null, error }
  }
}

// Corregir al responsable tiene que LLEGAR a personas. Sin esta ruta, editar el
// correo o el nombre no cambiaba nada (la ficha guardaba en familias.tutor_*, que
// el espejo vuelve a pisar) y cambiarle el telefono creaba una SEGUNDA persona --
// "el papa cambio de numero" es el caso mas comun que hay.
//
// El trigger trg_persona_espejo_familia es AFTER UPDATE OF nombres, apellidos,
// telefono, email: dispara por COLUMNA nombrada en el SET, no por valor. Por eso
// quien llama debe comparar antes y no escribir si nada cambio.
//
// HOY NO LA LLAMA NADIE, y no esta muerta: el alta dejo de corregir a las personas a
// proposito, y Pagos > Familias todavia lo hace de rebote (escribe familias.tutor_* y
// el trigger trg_familia_espejo_persona lo baja), que es al reves de como deberia ser.
// Cuando la Fase 3 retire las columnas tutor_*, esta va a ser la UNICA forma de editar
// a una persona.
export async function actualizarPersona(personaId, { nombres, apellidos, telefono, email }) {
  try {
    const { data, error } = await supabase
      .from('personas')
      .update({
        nombres: String(nombres || '').trim(),
        apellidos: String(apellidos || '').trim(),
        telefono: String(telefono || '').trim() || null,
        email: String(email || '').trim() || null,
      })
      .eq('id', personaId)
      .select()
      .single()
    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[actualizarPersona]', error)
    return { data: null, error }
  }
}

// Las fichas por completar de la org activa.
//
// Cinco categorias en UNA llamada, cada una con los datos que hacen falta para
// arreglarla (el nombre partido, el tutor capturado, los vinculos de la persona). Sin
// esos datos cada fila costaria otra consulta, y la pantalla no es un tablero de solo
// lectura: cada fila lleva a la correccion.
//
// p_org_id repite lo que hace applyOrgFilter(): null en el caso normal (el RLS clava
// la consulta a la org activa) y la org impersonada cuando un platform_admin la fija.
// El RPC es SECURITY INVOKER a proposito -- ver el encabezado de la migracion
// 20260827_fichas_incompletas.sql.
export async function getFichasIncompletas() {
  try {
    const { data, error } = await supabase.rpc('get_fichas_incompletas', {
      p_org_id: getActiveOrgOverride(),
    })
    if (error) throw error
    return { data: data || [], error: null }
  } catch (error) {
    console.error('[getFichasIncompletas]', error)
    // El RPC rechaza a un platform_admin sin org fijada: su RLS no estrecha nada, asi
    // que la lista mezclaria fichas de varias organizaciones -- y la pantalla ofreceria
    // unificar personas de orgs distintas. Se dice en castellano, no con un 22023.
    if (error?.code === '22023' || /falta la organizacion/i.test(error?.message || '')) {
      return {
        data: [],
        error: new Error('Elige una organización para ver sus fichas por completar.'),
      }
    }
    return { data: [], error }
  }
}

// Juntar varias personas en una. La otra mitad de la regla 5: se avisan las
// coincidencias y el usuario elige. Aqui eligio unificar.
export async function unificarPersonas(destinoId, origenIds = []) {
  try {
    if (!destinoId) return { data: null, error: new Error('Falta la ficha que se conserva.') }
    const origenes = (origenIds || []).filter((id) => id && id !== destinoId)
    if (!origenes.length) return { data: null, error: new Error('Selecciona al menos una ficha a unificar.') }

    const { data, error } = await supabase.rpc('unificar_personas', {
      p_destino: destinoId,
      p_origenes: origenes,
    })
    if (error) throw error
    if (data?.error) {
      const msg = {
        destino_inexistente: 'La ficha que se conserva ya no existe.',
        sin_acceso: 'No tienes permiso para unificar fichas de esta organización.',
        origen_de_otra_org: 'Alguna de las fichas pertenece a otra organización.',
        sin_origenes: 'Selecciona al menos una ficha a unificar.',
      }[data.error] || 'No se pudo unificar.'
      return { data: null, error: new Error(msg) }
    }
    return { data, error: null }
  } catch (error) {
    console.error('[unificarPersonas]', error)
    return { data: null, error }
  }
}

// "Son personas distintas": la otra respuesta de la regla 5, y hay que guardarla.
// En produccion 4 de los 6 grupos de telefono compartido son papa y mama de verdad;
// una lista de pendientes que no se puede vaciar se deja de leer.
//
// Se guarda el CONJUNTO que el humano vio, no solo el telefono: si manana entra otra
// persona con ese numero, el grupo vuelve a aparecer.
export async function descartarDuplicadoPersonas({ telefonoNorm, personaIds, orgId = null }) {
  try {
    const org = await orgActiva(orgId)
    const { error } = await supabase
      .from('personas_duplicado_descartado')
      .upsert({
        org_id: org,
        telefono_norm: String(telefonoNorm || ''),
        persona_ids: personaIds || [],
      }, { onConflict: 'org_id,telefono_norm' })
    if (error) throw error
    return { data: true, error: null }
  } catch (error) {
    console.error('[descartarDuplicadoPersonas]', error)
    return { data: null, error }
  }
}

// "Ya lo revise, esta bien asi": la diferencia que no es un error.
//
// Gemela de descartarDuplicadoPersonas, y con el mismo cuidado: se guarda LO QUE SE
// COMPARO. La firma concreta (consentimiento_id, que ya identifica al jugador porque hay
// una fila de consentimientos por jugador) y como se veia la ficha (nombre y correo de la
// persona) -- asi que una firma NUEVA que difiera de otra forma, o una edicion posterior
// de la persona, vuelven a listarse. Descartar esta comparacion no es "no me vuelvas a
// avisar de este jugador".
export async function descartarDiferenciaDeFirma({
  personaId, consentimientoId, fichaNombre = null, fichaEmail = null, orgId = null,
}) {
  try {
    if (!personaId || !consentimientoId) {
      throw new Error('Falta la persona o la firma que se está revisando.')
    }
    const org = await orgActiva(orgId)
    const { error } = await supabase
      .from('firma_diferencia_descartada')
      .upsert({
        org_id: org,
        persona_id: personaId,
        consentimiento_id: consentimientoId,
        ficha_nombre: fichaNombre,
        ficha_email: fichaEmail,
      }, { onConflict: 'consentimiento_id,persona_id' })
    if (error) throw error
    return { data: true, error: null }
  } catch (error) {
    console.error('[descartarDiferenciaDeFirma]', error)
    return { data: null, error }
  }
}

// "No tiene apellido / no lo sabemos": la salida que le faltaba a la unica categoria
// que no tenia ninguna.
//
// Tercera gemela de descartarDuplicadoPersonas y descartarDiferenciaDeFirma, con el
// mismo cuidado: se guarda EL NOMBRE COMO SE VEIA. Si despues editan a la persona, lo
// que se reviso ya no es lo que hay y la fila vuelve. (Ponerle apellido la saca sola.)
export async function descartarResponsableSinApellido({ personaId, nombreCompleto = null, orgId = null }) {
  try {
    if (!personaId) throw new Error('Falta la persona que se está revisando.')
    const org = await orgActiva(orgId)
    const { error } = await supabase
      .from('responsable_sin_apellido_descartado')
      .upsert({
        org_id: org,
        persona_id: personaId,
        nombre_completo: nombreCompleto,
      }, { onConflict: 'persona_id' })
    if (error) throw error
    return { data: true, error: null }
  } catch (error) {
    console.error('[descartarResponsableSinApellido]', error)
    return { data: null, error }
  }
}

// Quien es HOY el responsable principal de una familia.
//
// Se necesita para dos cosas distintas, y las dos son de correccion:
//  1. No reescribirlo. Sumar un hermano a una familia que ya tenia responsable
//     ligaba al capturado como principal: el espejo copiaba sus datos a
//     familias.tutor_*, y el OTP de consentimiento del hermano YA registrado se iba
//     al telefono nuevo (consent.js resuelve el destino como fam.tutor_telefono).
//  2. Reconocerlo cuando cambia de telefono: el numero nuevo no encuentra a nadie,
//     pero si el nombre es el suyo es la misma persona, y hay que actualizarla en
//     vez de duplicarla.
export async function responsablePrincipalDeFamilia(familiaId) {
  if (!familiaId) return { data: null, error: null }
  try {
    const { data, error } = await supabase
      .from('familia_personas')
      .select('persona_id, personas:persona_id ( id, nombres, apellidos, nombre_completo, telefono, email )')
      .eq('familia_id', familiaId)
      .eq('rol', 'responsable')
      .eq('es_principal', true)
      .maybeSingle()
    if (error) throw error
    return { data: data?.personas || null, error: null }
  } catch (error) {
    console.error('[responsablePrincipalDeFamilia]', error)
    return { data: null, error }
  }
}

// EL RESPONSABLE DE PAGO SE GUARDA EN PERSONAS, nunca en familias.tutor_*: desde
// 20260908b la base rechaza escribir esas columnas (son un espejo que se llena solo).
// Es la unica forma de corregir a un responsable desde Pagos > Familias.
//
// Si la familia ya tiene principal, se corrige a ESA persona -- "el papa cambio de
// numero" es el caso mas comun que hay, y crear otra persona era justo lo que
// duplicaba a la gente. Si no tiene, se crea y se liga. Con los campos vacios no se
// escribe nada: borrar a un responsable no es un caso que exista aqui.
export async function guardarResponsableDeFamilia(familiaId, { nombres, apellidos, telefono, email } = {}, orgId = null) {
  const limpio = (t) => String(t || '').trim()
  const vacio = !limpio(nombres) && !limpio(apellidos)
  const { data: principal, error: errPrincipal } = await responsablePrincipalDeFamilia(familiaId)
  if (errPrincipal) return { data: null, error: errPrincipal }

  if (principal?.id) {
    if (vacio) return { data: principal, error: null }
    const igual = limpio(nombres) === limpio(principal.nombres)
      && limpio(apellidos) === limpio(principal.apellidos)
      && soloDigitos(telefono) === soloDigitos(principal.telefono)
      && limpio(email) === limpio(principal.email)
    // El trigger espejo dispara por columna nombrada en el SET, no por valor:
    // no se escribe si nada cambio.
    if (igual) return { data: principal, error: null }
    return actualizarPersona(principal.id, { nombres, apellidos, telefono, email })
  }

  if (vacio) return { data: null, error: null }
  const { data: nueva, error: errCrear } = await crearPersona({ nombres, apellidos, telefono, email, orgId })
  if (errCrear) return { data: null, error: errCrear }
  const { error: errLigar } = await ligarPersonaAFamilia({ familiaId, personaId: nueva.id, rol: 'responsable' })
  return { data: nueva, error: errLigar || null }
}
